(function () {
  'use strict';

  const params = new URLSearchParams(window.location.search);
  const watchedParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
    'ttclid',
    'msclkid',
  ];
  const stored = {};
  let found = false;

  // Step 1: Capture parameters immediately
  watchedParams.forEach(function (key) {
    if (params.has(key)) {
      stored[key] = params.get(key);
      found = true;
    }
  });

  if (!found) return;

  // IMPORTANT: Filter out Rebuy navigation entirely
  // Rebuy is an on-site product recommendation app, not a traffic source
  // If utm_source=rebuy, ignore it completely - don't save, don't process
  if (stored.utm_source && stored.utm_source.toLowerCase() === 'rebuy') {
    // eslint-disable-next-line no-console -- verbatim debug logging preserved from original inline script
    console.log('[Attribution] Ignoring rebuy navigation - not a traffic source');
    return;
  }

  function mergeIntoLocalRecord(visit) {
    let prior = null;
    try {
      prior = JSON.parse(localStorage.getItem('coracrea_attribution') || 'null');
    } catch (_e) {
      // Malformed record: leave it for the tracker to replace
    }
    // Only a record the tracker already dated (it owns _timestamp and _expiry) is merged; a bare label would never expire.
    if (!prior || typeof prior !== 'object' || !prior._expiry) return;
    localStorage.setItem(
      'coracrea_attribution',
      JSON.stringify(window.CoraAttributionRule.mergeVisit(prior, visit))
    );
  }

  // The session copy feeds GA4, the pixels and the cart landing page and referrer; a new session starts from the plain-key shape the readers expect.
  function mergeIntoSessionRecord(visit) {
    let base = null;
    try {
      base = JSON.parse(sessionStorage.getItem('attribution') || 'null');
    } catch (_e) {
      // Malformed copy: start a new one
    }
    if (!base || typeof base !== 'object') {
      base = {
        source: stored.utm_source || 'direct',
        medium: stored.utm_medium || 'none',
        campaign: stored.utm_campaign || '(not set)',
        term: stored.utm_term || '',
        content: stored.utm_content || '',
        timestamp: Date.now(),
        landingPage: window.location.href,
        referrer: document.referrer || 'direct',
      };
    }
    sessionStorage.setItem(
      'attribution',
      JSON.stringify(window.CoraAttributionRule.mergeSessionVisit(base, visit))
    );
  }

  // Step 2: Store in sessionStorage immediately (before any delay)
  try {
    sessionStorage.setItem('utm_sanitizer', JSON.stringify(stored));

    // Attribution rule: a click-id URL is owned by snippets/click-id-early-capture.liquid; a utm-only URL is merged into both records through window.CoraAttributionRule, so this script never assigns a click-id field.
    if (window.CoraAttributionRule && !window.CoraAttributionRule.ownsUrl()) {
      const visit = {};
      if (stored.utm_source) {
        visit._source = stored.utm_source;
        visit._medium = stored.utm_medium || 'none';
      }
      if (stored.utm_campaign) visit._campaign = stored.utm_campaign;
      if (stored.utm_term) visit._term = stored.utm_term;
      if (stored.utm_content) visit._content = stored.utm_content;
      if (stored.utm_source) mergeIntoLocalRecord(visit);
      mergeIntoSessionRecord(visit);
    }
  } catch (e) {
    console.error('[Attribution] Storage failed:', e);
  }

  // Step 3: Wait for analytics to load before cleaning URL
  function waitForAnalytics(callback) {
    const maxWait = 10000; // 10 seconds max
    const startTime = Date.now();
    let checkCount = 0;

    const checkInterval = setInterval(function () {
      checkCount++;
      const ga4Ready = typeof window.gtag === 'function';
      const fbReady = typeof window.fbq === 'function';
      const elapsed = Date.now() - startTime;
      const timeout = elapsed > maxWait;

      // Debug logging
      if (checkCount % 10 === 0) {
        // Log every 1 second
        // eslint-disable-next-line no-console -- verbatim debug logging preserved from original inline script
        console.log(
          '[Attribution] Waiting for analytics... GA4:',
          ga4Ready,
          'FB:',
          fbReady,
          'Elapsed:',
          elapsed + 'ms'
        );
      }

      if ((ga4Ready && fbReady) || timeout) {
        clearInterval(checkInterval);
        // eslint-disable-next-line no-console -- verbatim debug logging preserved from original inline script
        console.log(
          '[Attribution] Analytics ready. GA4:',
          ga4Ready,
          'FB:',
          fbReady,
          'Timeout:',
          timeout
        );
        callback(ga4Ready, fbReady);
      }
    }, 100); // Check every 100ms
  }

  // Step 4: Explicitly send parameters to analytics
  function sendToAnalytics() {
    // Send to GA4
    if (typeof window.gtag === 'function') {
      try {
        window.gtag('set', {
          campaign_source: stored.utm_source || 'direct',
          campaign_medium: stored.utm_medium || 'none',
          campaign_name: stored.utm_campaign || '(not set)',
          campaign_term: stored.utm_term || '',
          campaign_content: stored.utm_content || '',
        });

        // Also send as event for verification
        window.gtag('event', 'attribution_captured', {
          source: stored.utm_source || 'direct',
          medium: stored.utm_medium || 'none',
          campaign: stored.utm_campaign || '(not set)',
          has_fbclid: !!stored.fbclid,
          has_gclid: !!stored.gclid,
        });

        // eslint-disable-next-line no-console -- verbatim debug logging preserved from original inline script
        console.log('[Attribution] Sent to GA4:', stored);
      } catch (e) {
        console.error('[Attribution] GA4 send failed:', e);
      }
    }

    // Send to Facebook Pixel
    if (typeof window.fbq === 'function' && stored.fbclid) {
      try {
        // Track with external_id for proper attribution
        window.fbq(
          'track',
          'PageView',
          {},
          {
            external_id: stored.fbclid,
          }
        );
        // eslint-disable-next-line no-console -- verbatim debug logging preserved from original inline script
        console.log('[Attribution] Sent fbclid to Meta Pixel:', stored.fbclid);
      } catch (e) {
        console.error('[Attribution] Meta Pixel send failed:', e);
      }
    }
  }

  // Step 5: Clean URL (only after analytics has parameters)
  function cleanURL() {
    try {
      watchedParams.forEach(function (key) {
        params.delete(key);
      });

      const newQuery = params.toString();
      const newUrl =
        window.location.pathname + (newQuery ? '?' + newQuery : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      // eslint-disable-next-line no-console -- verbatim debug logging preserved from original inline script
      console.log('[Attribution] URL cleaned');
    } catch (e) {
      console.error('[Attribution] URL cleaning failed:', e);
    }
  }

  // Execute the sequence
  waitForAnalytics(function (ga4Ready, fbReady) {
    // Send to analytics
    sendToAnalytics();

    // Clean URL after confirmation
    cleanURL();

    // Log final status
    // eslint-disable-next-line no-console -- verbatim debug logging preserved from original inline script
    console.log('[Attribution] Complete. GA4:', ga4Ready, 'FB:', fbReady);
  });
})();
